import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Paperclip, Send, MessageSquare, X, FileText } from "lucide-react";
import { useT } from "@/lib/i18n";

export default function CommentSection({ contributionId }) {
    const { t } = useT();
    const [comments, setComments] = useState([]);
    const [name, setName] = useState("");
    const [body, setBody] = useState("");
    const [attachment, setAttachment] = useState(null); // { url, name, size }
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        api.get("/comments", { params: { contribution_id: contributionId } })
            .then((r) => {
                if (!cancelled) setComments(r.data || []);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [contributionId]);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            alert("Max 5 MB");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setAttachment(data);
        } catch (err) {
            alert("Upload failed");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!body.trim()) return;
        setSubmitting(true);
        try {
            const payload = {
                contribution_id: contributionId,
                author_name: name.trim() || t("common.anonymous"),
                body: body.trim(),
                attachment_url: attachment?.url || null,
                attachment_name: attachment?.name || null,
            };
            const { data } = await api.post("/comments", payload);
            setComments((prev) => [...prev, data]);
            setBody("");
            setAttachment(null);
        } catch (err) {
            alert("Failed to post comment");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            data-testid={`comments-${contributionId}`}
            className="mt-6 pt-6 border-t border-black/5"
        >
            <div className="flex items-center gap-2 mb-4">
                <MessageSquare size={16} strokeWidth={1.6} className="text-brand-green" />
                <span className="caption text-brand-green">
                    {t("common.comments")} · {comments.length}
                </span>
            </div>

            {comments.length === 0 ? (
                <p className="text-sm text-ink-muted italic mb-4">{t("common.noComments")}</p>
            ) : (
                <ul className="space-y-3 mb-5">
                    {comments.map((c) => (
                        <li
                            key={c.id}
                            data-testid={`comment-${c.id}`}
                            className="bg-bg-alt/40 rounded-xl p-4 border border-black/5"
                        >
                            <div className="flex items-baseline justify-between gap-3 mb-1">
                                <span className="font-medium text-ink-primary text-sm">
                                    {c.author_name}
                                </span>
                                <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-ink-muted">
                                    {new Date(c.created_at).toLocaleDateString()}
                                </span>
                            </div>
                            <p className="text-[15px] text-ink-secondary leading-relaxed whitespace-pre-line">
                                {c.body}
                            </p>
                            {c.attachment_url && (
                                <a
                                    href={`${API.replace(/\/api$/, "")}${c.attachment_url}`}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="mt-2 inline-flex items-center gap-2 text-xs text-brand-blue hover:underline"
                                >
                                    <FileText size={12} strokeWidth={1.5} />
                                    {c.attachment_name || t("common.attachment")}
                                </a>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            <form
                onSubmit={handleSubmit}
                className="space-y-3"
                data-testid={`comment-form-${contributionId}`}
            >
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("common.yourName")}
                    maxLength={80}
                    data-testid={`comment-name-${contributionId}`}
                    className="w-full px-3 py-2 bg-white border border-black/10 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/30"
                />
                <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder={t("common.yourComment")}
                    rows={3}
                    required
                    maxLength={2000}
                    data-testid={`comment-body-${contributionId}`}
                    className="w-full px-3 py-2 bg-white border border-black/10 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/30 resize-none"
                />
                <div className="flex flex-wrap items-center gap-2">
                    <label className="inline-flex items-center gap-1.5 cursor-pointer px-3 py-2 rounded-lg border border-dashed border-black/15 hover:border-brand-green/40 text-xs text-ink-secondary transition-colors">
                        <Paperclip size={13} strokeWidth={1.6} />
                        {uploading ? t("common.uploading") : t("common.attachFile")}
                        <input
                            ref={fileInputRef}
                            type="file"
                            data-testid={`comment-file-${contributionId}`}
                            onChange={handleUpload}
                            accept=".png,.jpg,.jpeg,.webp,.gif,.pdf,.txt"
                            className="hidden"
                            disabled={uploading}
                        />
                    </label>
                    {attachment && (
                        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 bg-brand-green/10 text-brand-green rounded-md">
                            <FileText size={11} strokeWidth={1.6} />
                            {attachment.name}
                            <button
                                type="button"
                                aria-label="remove"
                                onClick={() => setAttachment(null)}
                                className="ml-1 opacity-70 hover:opacity-100"
                            >
                                <X size={11} strokeWidth={2} />
                            </button>
                        </span>
                    )}
                    <button
                        type="submit"
                        disabled={submitting || !body.trim()}
                        data-testid={`comment-submit-${contributionId}`}
                        className="ml-auto inline-flex items-center gap-2 bg-brand-green text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-brand-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Send size={13} strokeWidth={1.6} />
                        {submitting ? t("common.sending") : t("common.send")}
                    </button>
                </div>
            </form>
        </div>
    );
}
